// src/pages/Services.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../components/Button';

// Wrapper with background styling
const ServicesWrapper = styled.div`
  background-image: url('/images/services-bg.jpg'); /* Add a background image */
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  min-height: 100vh;
  padding-top: 80px; /* Space for the fixed navbar */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(240, 249, 255, 0.85); /* Semi-transparent light blue overlay */
    z-index: 0;
  }
`;

const ServicesContainer = styled.div`
  max-width: 1200px;
  margin: 3rem auto 4rem;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background-color: ${props => props.theme.colors.secondary};
    margin: 1rem auto;
  }
`;

const PageDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.text};
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.6;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
  
  .icon-container {
    background-color: ${props => props.theme.colors.background};
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .icon {
      font-size: 3rem;
      color: ${props => props.theme.colors.primary};
    }
  }
  
  .content {
    padding: 2rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    
    h3 {
      color: ${props => props.theme.colors.primary};
      margin-bottom: 1rem;
      font-size: 1.5rem;
      position: relative;
      padding-bottom: 0.8rem;
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 50px;
        height: 3px;
        background-color: ${props => props.theme.colors.secondary};
      }
    }
    
    p {
      color: ${props => props.theme.colors.lightText};
      margin-bottom: 1.5rem;
      line-height: 1.6;
      flex: 1;
    }
  }
`;

const ServiceButton = styled(Button)`
  width: 100%;
  margin-top: auto;
`;

const Services = () => {
  // Service data with proper IDs and icons
  const services = [
    {
      id: "dementia-care",
      title: "Dementia Care",
      description: "Specialized support for individuals living with dementia across the West Midlands, focusing on maintaining dignity and quality of life.",
      icon: "brain"
    },
    {
      id: "personal-care",
      title: "Personal Care",
      description: "Assistance with daily activities including bathing, dressing, medication management, and mobility support in the West Midlands.",
      icon: "hands-helping"
    },
    {
      id: "mental-health",
      title: "Mental Health Support",
      description: "Compassionate care for individuals with mental health conditions in the West Midlands, promoting emotional wellbeing.",
      icon: "heart-pulse"
    },
    {
      id: "disability-care",
      title: "Disability Care",
      description: "Specialized support for individuals with physical or learning disabilities across the West Midlands, focusing on promoting independence.",
      icon: "wheelchair"
    },
    {
      id: "elderly-care",
      title: "Elderly Care",
      description: "Compassionate support for older adults in the West Midlands, helping them maintain their independence and dignity.",
      icon: "user-nurse"
    },
    {
      id: "agency-staffing",
      title: "Agency Staffing",
      description: "Providing qualified and reliable agency staff to care homes and healthcare settings across the West Midlands.",
      icon: "user-tie" 
    },
    {
      id: "care-professionals",
      title: "Care Professionals",
      description: "Connecting skilled and compassionate care professionals with clients for various care requirements in the West Midlands.",
      icon: "hands-heart" 
    }
  ];
  
  return (
    <ServicesWrapper>
      <ServicesContainer>
        <PageTitle
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Our Services
        </PageTitle>
        
        <PageDescription
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          At L&J Agape Healthcare, we provide a comprehensive range of personalized care services 
          tailored to meet the unique needs of each client **across the West Midlands.**
        </PageDescription>
        
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard 
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="icon-container">
                <i className={`fas fa-${service.icon} icon`}></i>
              </div>
              <div className="content">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ServiceButton to={`/services/${service.id}`} variant="outline" color="secondary">
                  Learn More
                </ServiceButton>
              </div>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </ServicesContainer>
    </ServicesWrapper>
  );
};

export default Services;